import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { packsApi, getErrorMessage } from '../lib/api';
import toast from 'react-hot-toast';
import { fetchAllPages } from '../lib/pagination';
import type { Pack, CreatePackInput, PaginatedResponse } from '../lib/types';

export function usePacks(params?: {
  status?: string;
  customer?: string;
  search?: string;
  limit?: number;
  lastKey?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  page?: number;
  fetchAll?: boolean; // If true, fetches all pages (default: false to prevent excessive API calls)
}) {
  return useQuery<PaginatedResponse<Pack>>({
    queryKey: ['packs', params],
    queryFn: async () => {
      try {
        if (params?.fetchAll === true) {
          // Only fetch all pages if explicitly requested
          const allItems = await fetchAllPages<Pack>(
            (pageParams) => packsApi.list({ ...params, ...pageParams }),
            { limit: params?.limit || 500, maxPages: 20 }
          );
          return { items: allItems, lastKey: null };
        } else {
          // Fetch single page by default
          const result = await packsApi.list(params);
          return result;
        }
      } catch (error) {
        console.error('Error fetching packs:', error);
        throw error;
      }
    },
  });
}

export function usePack(id: string) {
  return useQuery<Pack>({
    queryKey: ['pack', id],
    queryFn: () => packsApi.get(id),
    enabled: !!id,
  });
}

export function useCreatePack() {
  const queryClient = useQueryClient();

  return useMutation<Pack, Error, CreatePackInput>({
    mutationFn: packsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['packs'] });
      queryClient.invalidateQueries({ queryKey: ['stats'] });
      toast.success('Pack created successfully');
    },
    onError: (error) => {
      toast.error(`Error creating pack: ${getErrorMessage(error)}`);
    },
  });
}

export function useDeletePack() {
  const queryClient = useQueryClient();

  return useMutation<{ message: string }, Error, string>({
    mutationFn: packsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['packs'] });
      queryClient.invalidateQueries({ queryKey: ['cells'] });
      queryClient.invalidateQueries({ queryKey: ['stats'] });
      toast.success('Pack deleted successfully');
    },
    onError: (error) => {
      toast.error(`Error deleting pack: ${getErrorMessage(error)}`);
    },
  });
}

export function useUpdatePack() {
  const queryClient = useQueryClient();

  return useMutation<Pack, Error, { id: string; data: Partial<CreatePackInput> }>({
    mutationFn: ({ id, data }) => packsApi.update(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['packs'] });
      queryClient.invalidateQueries({ queryKey: ['pack', data.pack_id] });
      toast.success('Pack updated successfully');
    },
    onError: (error) => {
      toast.error(`Error updating pack: ${getErrorMessage(error)}`);
    },
  });
}


